import { LngLatLike } from 'mapbox-gl';
import { Feature, FeatureCollection, Geometry, LineString, Position } from '@turf/helpers';
import turfAlong from '@turf/along';
import turfBearing from '@turf/bearing';
import { MapboxMapShim } from 'types/Map';
import { removePolylines } from 'utils/mapbox/overlays/polylines';

export function initializeFlyoverMap(
  map: MapboxMapShim,
  atMapGeojson: FeatureCollection<
    Geometry,
    {
      [name: string]: any;
    }
  >
): void {
  removePolylines(map, 'atMap-polylines');

  // Add a line feature and layer. This feature will get updated as we progress the animation
  map.addSource('line', {
    type: 'geojson',
    // Line metrics is required to use the 'line-progress' property
    lineMetrics: true,
    data: atMapGeojson
  });

  map.addLayer({
    type: 'line',
    source: 'line',
    id: 'line',
    paint: {
      'line-color': 'rgba(0,0,0,0)',
      'line-width': 5
    },
    layout: {
      'line-cap': 'round',
      'line-join': 'round'
    }
  });
}

export function computePositionValues(
  map: MapboxMapShim,
  path: Feature<
    LineString,
    {
      [name: string]: any;
    }
  >,
  pathDistance: number,
  percentageComplete: number,
  previousPosition: Position | null
): {
  bearing: number | null;
  elevation: number;
  lngLat: LngLatLike;
  position: Position;
} {
  // Get the new latitude and longitude by sampling along the path
  const position = turfAlong(path, pathDistance * percentageComplete).geometry.coordinates;
  const lngLat = {
    lng: position[0],
    lat: position[1]
  };

  const bearing = previousPosition ? turfBearing(previousPosition, position) : null;

  // Sample the terrain elevation. We round to an integer value to prevent showing a lot of digits during the animation.
  const elevation = Math.floor(
    // Do not use terrain exaggeration to get actual meter values
    map.queryTerrainElevation(lngLat, { exaggerated: false })
  );

  return {
    bearing,
    elevation,
    lngLat,
    position
  };
}
